import { displayDateUS } from "../../utils/dateUtils";

/** @param {unknown} value */
function escapeHtml(value) {
  if (value == null || value === "") return "";
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function formatDate(val) {
  if (!val) return "";
  const s = displayDateUS(val);
  return s || String(val);
}

function yn(val) {
  const s = val == null ? "" : String(val);
  if (s === "Yes" || s === "true" || s === "1") return "Yes";
  if (s === "No" || s === "false" || s === "0") return "No";
  return s ? escapeHtml(s) : "—";
}

/**
 * Matches TbTest.jsx fetch logic.
 * @param {{ status?: number, result?: unknown }} response
 * @param {string|number|undefined} targetUserId
 */
export function parseTbTestFromApiResponse(response, targetUserId) {
  if (!response || response.status !== 1) return null;
  const { result } = response;
  if (Array.isArray(result)) {
    return result.length > 0 ? result[0] : null;
  }
  if (result?.test) {
    return result.test;
  }
  if (result?.items && Array.isArray(result.items)) {
    const uid = parseInt(String(targetUserId), 10);
    return result.items.find((item) => item.userId === uid) || null;
  }
  if (
    result &&
    typeof result === "object" &&
    !Array.isArray(result) &&
    (result.id != null ||
      result.employeeName != null ||
      result.userId != null)
  ) {
    return result;
  }
  return result || null;
}

function signatureBlock(sig) {
  if (sig == null || sig === "") {
    return `<span class="sig-empty">—</span>`;
  }
  const s = String(sig);
  if (s.startsWith("data:")) {
    return `<img class="sig-img" src="${escapeHtml(s)}" alt="" />`;
  }
  return `<span class="sig-text">${escapeHtml(s)}</span>`;
}

function rowPair(label, valueHtml) {
  return `<tr><td class="tb-l">${escapeHtml(label)}</td><td class="tb-v">${valueHtml}</td></tr>`;
}

/**
 * @param {Record<string, unknown>} record
 * @param {{ companyName: string; logoUrl: string }} branding
 */
export function buildTbTestPrintHtml(record, branding) {
  const company = escapeHtml(branding.companyName || "Company");
  const logoUrl = escapeHtml(branding.logoUrl || "");

  const employeeName = escapeHtml(
    String(record.employeeName || record.applicantName || "").trim(),
  );
  const ssn = escapeHtml(String(record.socialSecurityNumber || ""));
  const position = escapeHtml(String(record.positionAppliedFor || ""));

  const screeningRows = [
    ["Have you ever had Tuberculosis?", yn(record.hadTuberculosis)],
    [
      "Have you ever had the BCG Vaccine? (If yes, you must provide a Chest X-Ray report)",
      yn(record.hadBcgVaccine),
    ],
    [
      "Have you ever had a Positive TB Skin Test? (If yes, you must provide a Chest X-Ray report)",
      yn(record.hadPositiveTbSkinTest),
    ],
  ]
    .map(([a, b]) => rowPair(a, b))
    .join("");

  const symptomRows = [
    ["Productive Cough", yn(record.hasProductiveCough)],
    ["Night Sweats", yn(record.hasNightSweats)],
    ["Fever", yn(record.hasFever)],
    ["Weight Loss", yn(record.hasWeightLoss)],
    ["Loss of Appetite", yn(record.hasLossOfAppetite)],
  ]
    .map(([a, b]) => rowPair(a, b))
    .join("");

  const site = escapeHtml(String(record.site || ""));
  const ppd = escapeHtml(String(record.ppdResult || ""));
  const induration =
    record.indurationMm != null && record.indurationMm !== ""
      ? escapeHtml(String(record.indurationMm))
      : "—";

  const es = record.employeeSignature;
  const nestedEmpSig =
    es && typeof es === "object"
      ? /** @type {{ signature?: string }} */ (es).signature
      : null;
  const applicantSig =
    record.applicantSignature ||
    record.empSignature ||
    nestedEmpSig;
  const applicantSignedAt = escapeHtml(
    formatDate(
      record.applicantSignedAt ||
        record.applicantSignatureDate ||
        record.empSignedAt,
    ),
  );
  const applicantSignedBy = escapeHtml(
    String(record.applicantSignedBy || "").trim(),
  );

  const reviewedBySignedBy = escapeHtml(
    String(record.reviewedBySignedBy || "").trim(),
  );
  const reviewedBySig =
    record.reviewedBySignature || record.adminSignature;
  const reviewedBySignedAt = escapeHtml(
    formatDate(
      record.reviewedBySignedAt ||
        record.adminSignatureDate ||
        record.reviewedBySignatureDate,
    ),
  );

  const displayName = employeeName || applicantSignedBy || "—";

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <title>TB screening — ${employeeName || "Employee"}</title>
  <style>
    @page { margin: 0.4in; size: letter; }
    * { box-sizing: border-box; }
    body {
      font-family: ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Arial, sans-serif;
      font-size: 10px;
      line-height: 1.4;
      color: #111827;
      margin: 0;
      padding: 10px;
    }
    .sheet { max-width: 900px; margin: 0 auto; }
    @media screen {
      html {
        background: linear-gradient(168deg, #cbd5e1 0%, #e2e8f0 42%, #f1f5f9 100%);
        min-height: 100%;
      }
      body { padding: 26px 16px 36px; background: transparent; min-height: 100vh; }
      .sheet {
        background: #fff;
        padding: 28px 30px 34px;
        border: 1px solid #e2e8f0;
        box-shadow: 0 1px 2px rgba(15,23,42,0.05), 0 24px 48px -16px rgba(15,23,42,0.2);
      }
    }
    @media print {
      html { background: #fff !important; }
      body {
        -webkit-print-color-adjust: exact;
        print-color-adjust: exact;
        padding: 0 !important;
        margin: 0;
        background: #fff !important;
      }
      .sheet { padding: 0 !important; border: none !important; box-shadow: none !important; }
    }
    .top {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: 14px;
      padding-bottom: 10px;
      border-bottom: 1px solid #e5e7eb;
      margin-bottom: 12px;
    }
    .logo { height: 50px; width: auto; max-width: 190px; object-fit: contain; }
    .title-block { text-align: right; flex: 1; }
    .title-main { font-size: 15px; font-weight: 700; margin: 0 0 3px 0; }
    .title-sub { font-size: 11px; font-weight: 500; color: #2563eb; margin: 0; }
    .meta {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      gap: 10px;
      margin-bottom: 14px;
    }
    .fl { font-size: 8px; font-weight: 600; color: #6b7280; text-transform: uppercase; margin-bottom: 3px; }
    .fv {
      border: 1px solid #e5e7eb;
      border-radius: 6px;
      padding: 6px 8px;
      min-height: 30px;
      font-size: 10px;
    }
    .sec {
      margin-bottom: 12px;
      border: 1px solid #e2e8f0;
      border-radius: 8px;
      overflow: hidden;
    }
    .sec-h {
      background: #f8fafc;
      padding: 8px 10px;
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      border-bottom: 1px solid #e2e8f0;
    }
    .tb { width: 100%; border-collapse: collapse; font-size: 9.5px; }
    .tb-l { padding: 6px 10px; border-top: 1px solid #f1f5f9; width: 72%; color: #334155; }
    .tb-v { padding: 6px 10px; border-top: 1px solid #f1f5f9; font-weight: 600; color: #0f172a; }
    .ppd-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 8px 12px;
      padding: 10px;
      background: #fafafa;
    }
    .two-col {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 12px;
      margin-top: 14px;
    }
    .sig-card {
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 10px;
      background: #fafafa;
    }
    .sig-card h3 { font-size: 11px; font-weight: 700; margin: 0 0 8px 0; }
    .sig-label { font-size: 8px; font-weight: 600; color: #6b7280; margin: 6px 0 3px 0; }
    .sig-line {
      border-bottom: 1px solid #9ca3af;
      min-height: 24px;
      font-size: 10px;
      padding: 3px 0;
    }
    .sig-img { max-height: 40px; max-width: 100%; object-fit: contain; }
    .sig-empty { color: #9ca3af; }
    .footer-print {
      margin-top: 16px;
      padding-top: 8px;
      border-top: 1px solid #e5e7eb;
      text-align: center;
      font-size: 8px;
      color: #6b7280;
    }
  </style>
</head>
<body>
  <div class="sheet">
    <div class="top">
      <img class="logo" src="${logoUrl}" alt="" onerror="this.style.display='none'" />
      <div class="title-block">
        <p class="title-main">Tuberculosis (TB) screening questionnaire</p>
        <p class="title-sub">${company}</p>
      </div>
    </div>

    <div class="meta">
      <div><div class="fl">Employee name</div><div class="fv">${employeeName || "—"}</div></div>
      <div><div class="fl">Social Security Number</div><div class="fv">${ssn || "—"}</div></div>
      <div><div class="fl">Position applied for</div><div class="fv">${position || "—"}</div></div>
    </div>

    <div class="sec">
      <div class="sec-h">1. Screening questions</div>
      <table class="tb"><tbody>${screeningRows}</tbody></table>
    </div>

    <div class="sec">
      <div class="sec-h">2. Symptoms review</div>
      <p style="margin:8px 10px 0;font-size:9px;color:#64748b;font-style:italic;">Answer Yes if you have experienced any of the following symptoms recently.</p>
      <table class="tb"><tbody>${symptomRows}</tbody></table>
    </div>

    <div class="sec">
      <div class="sec-h">TB skin test (PPD) — healthcare professional</div>
      <div class="ppd-grid">
        <div><div class="fl">Healthcare professional name</div><div class="fv">${escapeHtml(String(record.healthcareProfessionalName || "")) || "—"}</div></div>
        <div><div class="fl">Site</div><div class="fv">${site || "—"}</div></div>
        <div><div class="fl">Date placed</div><div class="fv">${escapeHtml(formatDate(record.datePlaced)) || "—"}</div></div>
        <div><div class="fl">Date read</div><div class="fv">${escapeHtml(formatDate(record.dateRead)) || "—"}</div></div>
        <div><div class="fl">Induration (mm)</div><div class="fv">${induration}</div></div>
        <div><div class="fl">Lot number</div><div class="fv">${escapeHtml(String(record.lotNumber || "")) || "—"}</div></div>
        <div><div class="fl">Expiration date</div><div class="fv">${escapeHtml(formatDate(record.expirationDate)) || "—"}</div></div>
        <div><div class="fl">PPD result</div><div class="fv">${ppd || "—"}</div></div>
        <div><div class="fl">Administered by — name</div><div class="fv">${escapeHtml(String(record.administeredByName || "")) || "—"}</div></div>
        <div><div class="fl">Administered by — title</div><div class="fv">${escapeHtml(String(record.administeredByTitle || "")) || "—"}</div></div>
        <div><div class="fl">Results read by — name</div><div class="fv">${escapeHtml(String(record.resultsReadByName || "")) || "—"}</div></div>
        <div><div class="fl">Results read by — title</div><div class="fv">${escapeHtml(String(record.resultsReadByTitle || "")) || "—"}</div></div>
      </div>
    </div>

    <div class="two-col">
      <div class="sig-card">
        <h3>Employee</h3>
        <div class="sig-label">Name</div>
        <div class="fv" style="margin-bottom:6px;">${displayName}</div>
        <div class="sig-label">Signature</div>
        <div class="sig-line">${signatureBlock(applicantSig)}</div>
        <div class="sig-label">Date</div>
        <div class="sig-line">${applicantSignedAt || "—"}</div>
      </div>
      <div class="sig-card">
        <h3>Admin / reviewer</h3>
        <div class="sig-label">Name</div>
        <div class="fv" style="margin-bottom:6px;">${reviewedBySignedBy || "—"}</div>
        <div class="sig-label">Signature</div>
        <div class="sig-line">${signatureBlock(reviewedBySig)}</div>
        <div class="sig-label">Date</div>
        <div class="sig-line">${reviewedBySignedAt || "—"}</div>
      </div>
    </div>

    <div class="footer-print">${company}</div>
  </div>
</body>
</html>`;
}
